import type {ReelMode, VideoMode} from '../../../types';
import {
  CreateForm,
  DEFAULT_MAX_UPLOAD_MB,
  STOCK_VISUALS,
  StudioOptions,
  VIDEO_TYPES,
  type VideoType,
} from '../createOptions';

/**
 * What the create form offers follows from the server's options and what the author picked so
 * far. CreateStore shows each of these as a computed value.
 */
export interface ChoiceState {
  organizationId: string | undefined;
  options: StudioOptions;
  form: CreateForm;
  source: {video: boolean; isDocument: boolean};
  videoMode: VideoMode;
  reelMode: ReelMode;
}

export const capabilities = ({options}: ChoiceState) =>
  options.capabilities || {};

/** Videos need a server that transcribes them and a library to upload them to. */
export const videosAllowed = (state: ChoiceState) =>
  capabilities(state).video === true && !!state.organizationId;

/** Documents are held to the server's limit; videos keep their own. */
export function maxDocumentMb({options: {maxUploadMb}}: ChoiceState) {
  return typeof maxUploadMb === 'number' && maxUploadMb > 0
    ? maxUploadMb
    : DEFAULT_MAX_UPLOAD_MB;
}

// A server that can cut clips says so; on an older one a video is always
// animated and keeps the animated settings.
export const canClip = (state: ChoiceState) =>
  state.source.video && capabilities(state).videoClipping === true;

// Clipped reels keep the video's own picture and sound, so there is no
// music, visuals or voice to choose.
export const clipping = (state: ChoiceState) =>
  canClip(state) && state.videoMode === 'clipping';

// Each topic becomes its own microreel, so the author picks topics instead
// of writing a focus; a focus typed earlier is kept but not sent.
export const perTopic = ({source, reelMode}: ChoiceState) =>
  source.isDocument && reelMode === 'per_topic';

/** Stock footage needs a server with Storyblocks; without it every microreel has still images. */
export const videoTypeChoices = (state: ChoiceState) =>
  VIDEO_TYPES.filter(
    item => item.id === 'still' || capabilities(state).storyblocks === true,
  );

/** The type is asked for only where there is a choice. */
export const showVideoType = (state: ChoiceState) =>
  videoTypeChoices(state).length > 1;

// The form keeps the brief's media source: any but AI images is stock footage, where the
// server has it.
export const videoType = (state: ChoiceState): VideoType =>
  state.form.mediaSource !== 'ai' && capabilities(state).storyblocks === true
    ? 'stock'
    : 'still';

// Stock footage fills every scene's picture, and a video has no pages to show, so neither
// offers "Use source content"; an earlier choice of it becomes a mix. With footage the
// choices say footage where they say AI images.
export const visualChoices = (state: ChoiceState) => {
  const stock = videoType(state) === 'stock';
  return state.options.visuals
    .filter(item => item.id !== 'source' || !(state.source.video || stock))
    .map(item =>
      stock && STOCK_VISUALS[item.id]
        ? {...item, ...STOCK_VISUALS[item.id]}
        : item,
    );
};

export const visuals = (state: ChoiceState) =>
  (state.source.video || videoType(state) === 'stock') &&
  state.form.visuals === 'source'
    ? 'partial'
    : state.form.visuals;

// Motion design animates new scenes; a clipped video keeps its own picture and sound.
export const showMotion = (state: ChoiceState) =>
  !!state.options.motionDesign && !clipping(state);

export const showImageStyle = (state: ChoiceState) =>
  state.options.imageStyles.length > 0 &&
  !clipping(state) &&
  visuals(state) !== 'source' &&
  videoType(state) === 'still';
